import { useEffect, useRef, useState } from 'react'

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: object) => void
          renderButton: (element: HTMLElement, config: object) => void
          prompt: () => void
        }
      }
    }
  }
}

interface Props {
  onSuccess: (credential: string) => void
  onError?: (error: string) => void
  text?: 'signin_with' | 'signup_with' | 'continue_with'
}

export default function GoogleSignInButton({ onSuccess, onError, text = 'continue_with' }: Props) {
  const buttonRef = useRef<HTMLDivElement>(null)
  const [googleFailed, setGoogleFailed] = useState(false)

  useEffect(() => {
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID
    if (!clientId) return

    let attempts = 0
    const maxAttempts = 30  // 3 seconds max (30 x 100ms)

    const tryRender = () => {
      attempts++

      if (window.google?.accounts?.id) {
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: (response: any) => { // eslint-disable-line @typescript-eslint/no-explicit-any
            if (response.credential) {
              onSuccess(response.credential)
            } else {
              onError?.('Google sign-in failed')
            }
          },
          auto_select: false,
          cancel_on_tap_outside: true,
        })

        if (buttonRef.current) {
          window.google.accounts.id.renderButton(
            buttonRef.current,
            {
              theme: 'filled_black',
              size: 'large',
              text: text,
              shape: 'rectangular',
              width: buttonRef.current.offsetWidth || 400,
              logo_alignment: 'left',
            }
          )
        }
        return
      }

      if (attempts < maxAttempts) {
        setTimeout(tryRender, 100)
      } else {
        setGoogleFailed(true)
        onError?.('Google Sign-In could not load. Please try again.')
      }
    }

    tryRender()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  if (googleFailed) {
    return (
      <button
        onClick={() => window.google?.accounts?.id?.prompt()}
        style={{
          width: '100%',
          minHeight: '44px',
          background: '#fff',
          color: '#3c4043',
          border: '1px solid #dadce0',
          borderRadius: '4px',
          fontSize: '14px',
          fontFamily: 'Google Sans, sans-serif',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
        }}
      >
        <svg width="18" height="18" viewBox="0 0 18 18">
          <path fill="#4285F4" d="M16.51 8H8.98v3h4.3c-.18 1-.74 1.48-1.6 2.04v2.01h2.6a7.8 7.8 0 002.38-5.88c0-.57-.05-.66-.15-1.18z"/>
          <path fill="#34A853" d="M8.98 17c2.16 0 3.97-.72 5.3-1.94l-2.6-2a4.8 4.8 0 01-7.18-2.54H1.83v2.07A8 8 0 008.98 17z"/>
          <path fill="#FBBC05" d="M4.5 10.52a4.8 4.8 0 010-3.04V5.41H1.83a8 8 0 000 7.18l2.67-2.07z"/>
          <path fill="#EA4335" d="M8.98 4.18c1.17 0 2.23.4 3.06 1.2l2.3-2.3A8 8 0 001.83 5.4L4.5 7.49a4.77 4.77 0 014.48-3.3z"/>
        </svg>
        Continue with Google
      </button>
    )
  }

  return (
    <div
      ref={buttonRef}
      style={{ width: '100%', minHeight: '44px' }}
    />
  )
}
